import { BreakoutButton } from './BreakoutButton'
import { Dialog, InlineDialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { useTranslationTyped } from 'i18n/i18n'
import { DateTime } from 'luxon'
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import type { FieldError } from 'react-hook-form'
import { FormError } from './form/FormError'
import type { FormFieldDefaults } from './types'
import { CalendarIcon } from 'components/icons/Calendar'
import { Button } from 'components/ui/button'
import { Calendar } from 'components/ui/calendar'
import { Field, FieldGroup } from 'components/ui/field'
import { Input } from 'components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover'
import { ChevronDownIcon } from 'lucide-react'
import { FormLabel } from './form/FormLabel'
import classNames from 'classnames'

/** 1min resolution has a huge dropdown so is intended for internal use only. */
type DateTimeInterval = '1min' | '15min' | '1day'

// eslint-disable-next-line react-refresh/only-export-components
export const FixedTimeComponents = {
  midnight: { hour: 0, minute: 0 },
  endOfDay: { hour: 23, minute: 59 },
}

function roundToNearestInterval(
  now: DateTime,
  interval: DateTimeInterval
): DateTime {
  if (interval === '1day') return now.startOf('day')
  const step = interval === '15min' ? 15 : 1
  const remainder = now.minute % step
  return remainder === 0
    ? now.set({ second: 0, millisecond: 0 })
    : now.plus({ minutes: step - remainder }).set({ second: 0, millisecond: 0 })
}

type ResolutionProp =
  | {
      interval: Extract<DateTimeInterval, '1day'>
      fixedTimeComponent: { hour: number; minute: number }
    }
  | Exclude<DateTimeInterval, '1day'>

type BreakoutDateTimeProps = Omit<
  FormFieldDefaults<'input'>,
  'value' | 'onChange' | 'max' | 'min' | 'defaultValue'
> & {
  kind?: 'secondary' | 'tertiary'
  LeadingIcon?: React.ComponentType<
    { size: number } & React.SVGProps<SVGSVGElement>
  >
  iconClassName?: string
  inputClassName?: string
  min?: DateTime
  max?: DateTime
  emptyText?: string
  value?: DateTime | null
  initialValue?: DateTime | null
  defaultValue?: DateTime
  hideNowButton?: boolean
  initialView?: DateTime
  force12HourMode?: boolean
  showInaccessibleTimes?: boolean
  onChange?: (value: DateTime | null) => void
  resolution: ResolutionProp
}

export const BreakoutDateTimeInput = forwardRef<
  HTMLInputElement,
  BreakoutDateTimeProps
>(function BreakoutDateTimeInput(props, fwdRef) {
  const {
    value: _value,
    defaultValue,
    onChange,
    min,
    max,
    error,
    errorClass,
    disabled,
    label,
    name,
    required,
    resolution,
  } = props

  const id = useId()
  const { tt } = useTranslationTyped()
  const resolutionInterval =
    typeof resolution === 'string' ? resolution : resolution.interval
  const isDateOnly = resolutionInterval === '1day'

  const [innerValue, setInnerValue] = useState<DateTime | null | undefined>(
    defaultValue ? defaultValue : null
  )

  useEffect(() => {
    if (_value === undefined) return
    setInnerValue(_value)
  }, [_value])

  const value = _value !== undefined ? _value : innerValue

  const [open, setOpen] = useState(false)
  const innerRef = useRef<HTMLInputElement | null>(null)

  const minDate = min ? min.startOf('day').toJSDate() : undefined
  const maxDate = max ? max.endOf('day').toJSDate() : undefined

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        e.preventDefault()
        setOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown, { capture: true })
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true })
    }
  }, [open])

  const updateValue = useCallback(
    (newValue: DateTime | null) => {
      let adjusted = newValue
      if (
        adjusted &&
        typeof resolution === 'object' &&
        resolution.interval === '1day'
      ) {
        adjusted = adjusted.set({
          hour: resolution.fixedTimeComponent.hour,
          minute: resolution.fixedTimeComponent.minute,
          second: 0,
          millisecond: 0,
        })
      }
      setInnerValue(adjusted)
      onChange?.(adjusted)
    },
    [onChange, resolution]
  )

  return (
    <div className="flex flex-col items-start">
      {/* Hidden native input for form compatibility */}
      <div className="relative h-0 w-0 overflow-hidden">
        <input
          ref={(e) => {
            innerRef.current = e
            if (!fwdRef) return
            if (typeof fwdRef === 'function') {
              fwdRef(e)
            } else {
              fwdRef.current = e
            }
          }}
          required={required}
          name={name}
          id={id}
          disabled={disabled}
          data-testid={`datetime-input-${name}`}
          type="datetime-local"
          className="absolute hidden opacity-0"
          onChange={(e) => {
            const datetime = e.target.value
              ? DateTime.fromISO(e.target.value)
              : null
            updateValue(datetime)
          }}
          value={
            value
              ? value.toFormat('yyyy-LL-dd') + 'T' + value.toFormat('HH:mm')
              : ''
          }
        />
      </div>

      <FieldGroup className="flex-row gap-3">
        <Field className="gap-1">
          <FormLabel
            for={`date-picker-${id}`}
            required={required}
            label={label ?? tt.design_system.date()}
          />
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                autoFocus={props.autoFocus}
                variant="outline"
                id={`date-picker-${id}`}
                disabled={disabled}
                data-testid={`datetime-field-${name}`}
                className="focus-ring-edge h-[3.25rem] w-full justify-between rounded-2xl bg-core-tertiary px-4 !text-label-medium font-normal outline outline-1 outline-outline-light"
              >
                <span
                  className={classNames('inline-flex items-center gap-1', {
                    'text-on-surface-var': !value,
                  })}
                >
                  <CalendarIcon
                    aria-hidden="true"
                    size={10}
                    className="mb-[1px] !size-auto"
                  />
                  {value
                    ? value.toLocaleString(DateTime.DATE_FULL)
                    : tt.design_system.select_date()}
                </span>
                <span className="sr-only">{tt.design_system.edit_date()}</span>
                <ChevronDownIcon aria-hidden="true" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto overflow-hidden p-0"
              align="start"
            >
              <Calendar
                mode="single"
                selected={value?.toJSDate()}
                captionLayout="dropdown"
                defaultMonth={value?.toJSDate()}
                onSelect={(date) => {
                  if (!date) return
                  const luxonDate = DateTime.fromJSDate(date)
                  const current =
                    value ??
                    roundToNearestInterval(DateTime.now(), resolutionInterval)
                  const merged = current.set({
                    year: luxonDate.year,
                    month: luxonDate.month,
                    day: luxonDate.day,
                  })
                  updateValue(merged)
                  setOpen(false)
                }}
                disabled={[
                  ...(minDate ? [{ before: minDate }] : []),
                  ...(maxDate ? [{ after: maxDate }] : []),
                ]}
              />
              <div className="border-t px-3 pb-2 pt-1">
                <Button
                  variant="ghost"
                  data-testid="datetime-picker-now"
                  className="h-8 w-full !text-label-medium"
                  onClick={() => {
                    updateValue(
                      roundToNearestInterval(DateTime.now(), resolutionInterval)
                    )
                    setOpen(false)
                  }}
                >
                  {isDateOnly
                    ? tt.design_system.select_today()
                    : tt.design_system.select_now()}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </Field>
        {!isDateOnly && (
          <Field className="w-32 gap-1">
            <FormLabel
              for={`time-picker-${id}`}
              required={required}
              label={tt.design_system.time()}
            />
            <Input
              type="time"
              step={resolutionInterval === '1min' ? 60 : 900}
              id={`time-picker-${id}`}
              disabled={disabled}
              data-testid={`datetime-time-${name}`}
              value={value ? value.toFormat('HH:mm') : ''}
              onChange={(e) => {
                const timeStr = e.target.value
                if (!timeStr) return
                const [hours, minutes] = timeStr.split(':').map(Number)
                const current = value ?? DateTime.now()
                const merged = current.set({
                  hour: hours,
                  minute: minutes,
                  second: 0,
                  millisecond: 0,
                })
                updateValue(merged)
              }}
              style={
                !value ? { color: 'var(--surfaces-on-surface-var)' } : undefined
              }
              className="focus-ring-edge h-[3.25rem] appearance-none rounded-2xl bg-core-tertiary px-4 text-label-medium outline outline-1 outline-outline-light [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
            />
          </Field>
        )}
      </FieldGroup>
      <FormError error={error} errorClass={errorClass} />
    </div>
  )
})

export function DatePickerDialog({
  value,
  show,
  onChange,
  onCancel,
  min,
  max,
  header,
  resolution = '15min',
  transitionDuration,
  useInlineDialog = true,
}: {
  value: DateTime | null
  content: string
  show: boolean
  hideNowButton?: boolean
  error: FieldError | undefined
  onChange: (dateTime: DateTime | null) => void
  onCancel: () => void
  header?: string | React.ReactNode
  min?: DateTime
  max?: DateTime
  initialView?: DateTime
  initialValue?: DateTime
  force12HourMode?: boolean
  transitionDuration?: number
  useInlineDialog?: boolean
  showInaccessibleTimes?: boolean
  resolution?: ResolutionProp
}) {
  const DialogToUse = useInlineDialog ? InlineDialog : Dialog
  const { tt } = useTranslationTyped()
  const resolutionInterval =
    typeof resolution === 'string' ? resolution : resolution.interval
  const isDateOnly = resolutionInterval === '1day'
  const defaultHeader = isDateOnly
    ? tt.design_system.choose_date()
    : tt.design_system.choose_date_and_time()
  const resolvedHeader = header ?? defaultHeader
  const buttonRef = useRef<HTMLButtonElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [selectedValue, setSelectedValue] = useState<DateTime | null>(value)

  return (
    <DialogToUse
      open={show}
      onDismiss={() => onCancel()}
      size="custom"
      className="relative min-w-[400px] max-w-[400px] rounded-3xl bg-surface-bright p-13"
      transitionDuration={transitionDuration}
      dismissOnEscapeKey
      testId="datetime-dialog"
      aria-labelledby={undefined}
      aria-label={tt.inputs.date_time_picker()}
    >
      <DialogCloseButton
        buttonRef={buttonRef}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onCancel()
          return false
        }}
      />
      <div className="mb-3 text-left text-headline-medium">
        {resolvedHeader}
      </div>
      <div className="py-4" ref={contentRef}>
        <BreakoutDateTimeInput
          name="datepicker"
          autoFocus
          value={selectedValue}
          onChange={setSelectedValue}
          min={min}
          max={max}
          resolution={resolution}
        />
      </div>
      <BreakoutButton
        data-testid="datetime-picker-ok"
        kind="primary"
        size="large"
        fullWidth
        className="mt-2"
        disabled={!selectedValue}
        onClick={() => {
          if (selectedValue) onChange(selectedValue)
        }}
      >
        {tt.design_system.choose_date()}
      </BreakoutButton>
    </DialogToUse>
  )
}
